import { Link as LinkIcon } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Spinner } from '../ui/spinner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { format, fromUnixTime } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { AutoSizer, Column, Table } from 'react-virtualized';
import { Actions as PAYMENT_ACTIONS } from '../../store/actions/paymentActions';
import { Actions as ORGANISATION_ACTIONS } from '../../store/actions/organisationActions';
import { getInvoiceRefundBadgeLabel } from '../utils/billingStatement';
import PlanEntitlementsSummary from '../Organisations/PlanEntitlementsSummary';
import CancelSubscriptionButton from './CancelSubscriptionButton';
import BillingStatementSection from './BillingStatementSection';
import ListPaymentMethods from './ListPaymentMethods';

const BillingPage = ({
  branding,
  getStripeInvoices,
  getStripePaymentMethods,
  gettingInvoices,
  invoices,
  cyfAdmin,
  currentOrg,
  ambassadorAdmin,
  stripeCustomerId,
  orgDetails,
  getOrganisation,
}) => {
  const [data, setData] = useState([]);
  const { headers } = branding?.fonts[0]?.font || {};

  useEffect(() => {
    if (!currentOrg) return;
    getStripeInvoices();
    getOrganisation();
  }, [currentOrg, getStripeInvoices, getOrganisation]);

  useEffect(() => {
    if (currentOrg && stripeCustomerId) {
      getStripePaymentMethods();
    }
  }, [currentOrg, stripeCustomerId, getStripePaymentMethods]);

  useEffect(() => setData(invoices), [invoices]);

  const cellValue = ({ cellData, rowData, dataKey }) => {
    const { status, amount_paid } = rowData;
    if (dataKey === 'status') {
      const refundLabel = getInvoiceRefundBadgeLabel(rowData.refund_status);
      return (
        <div className='flex flex-col items-center gap-1'>
          <span>{cellData ? cellData.charAt(0).toUpperCase() + cellData.slice(1) : ''}</span>
          {refundLabel && (
            <Badge variant='secondary' className='text-[10px] font-normal bg-info-bg text-info border-transparent'>
              {refundLabel}
            </Badge>
          )}
        </div>
      );
    } else if (dataKey === 'created') {
      return format(fromUnixTime(cellData), 'dd/MM/yyyy');
    } else if (dataKey === 'period') {
      return (
        format(fromUnixTime(rowData.period_start), 'dd/MM/yyyy') +
        ' - ' +
        format(fromUnixTime(rowData.period_end), 'dd/MM/yyyy')
      );
    } else if (dataKey === 'total' || dataKey === 'amount') {
      const currency = rowData.currency.toUpperCase();
      const price = amount_paid !== undefined || amount_paid !== null ? amount_paid / 100 : null;
      const amountRefunded =
        rowData.amount_refunded != null && rowData.amount_refunded > 0 ? rowData.amount_refunded / 100 : null;
      return (
        <div className='flex flex-col items-center'>
          <span>{amount_paid !== undefined || amount_paid !== null ? `${price} ${currency}` : ''}</span>
          {amountRefunded != null && (
            <span className='text-[10px] text-muted-foreground'>Refunded {amountRefunded} {currency}</span>
          )}
        </div>
      );
    } else if (dataKey === 'description') {
      const { lines: { data } = {} } = rowData;
      return data && data.length > 0 ? data[0].description : '';
    }
    if (dataKey === 'receipt' && status === 'paid') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <a className='cursor-pointer text-primary underline' href={rowData.hosted_invoice_url} target='_blank' rel='noreferrer'>
                  <LinkIcon size={16} strokeWidth={1.5} style={{ marginLeft: 5 }} />
                </a>
              </TooltipTrigger>
              <TooltipContent>
                <p style={{ fontSize: 12, marginBottom: 0 }}>Payment Receipt</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    }
    if (dataKey === 'invoice') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <a className='cursor-pointer text-primary underline' href={rowData.invoice_pdf} target='_blank' rel='noreferrer'>
                  <LinkIcon size={16} strokeWidth={1.5} />
                </a>
              </TooltipTrigger>
              <TooltipContent>
                <p style={{ fontSize: 12, marginBottom: 0 }}>Itemised Invoice</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    }
    return cellData;
  };

  return (
    <section className='panel' style={{ fontFamily: headers }}>
      <header
        className='panel-heading'
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        Billing Page
      </header>
      <div className='panel-body' style={{ overflow: 'auto' }}>
        {currentOrg && <PlanEntitlementsSummary organisation={orgDetails} className='mb-4' />}
        {!ambassadorAdmin && currentOrg && stripeCustomerId && (
          <div className='p-3 mb-3 border-b border-border'>
            <h3 className='text-sm font-semibold text-foreground mb-2'>Subscription</h3>
            <CancelSubscriptionButton subLabel='main subscription' />
          </div>
        )}
        {!ambassadorAdmin && currentOrg && stripeCustomerId && <ListPaymentMethods />}
        {!ambassadorAdmin && currentOrg && stripeCustomerId && (
          <BillingStatementSection mode='customer' />
        )}
        {gettingInvoices && <div className='flex justify-center py-4'><Spinner /></div>}
        {!data?.length > 0 && !gettingInvoices && (
          <Alert id='report-not-found' variant='warning' className='text-[15px] w-1/2'>
            <AlertTitle>Not Found</AlertTitle>
            <AlertDescription>There are no invoices on record for your company.</AlertDescription>
          </Alert>
        )}
        <div style={{ width: '100%', height: 500, display: !data?.length > 0 ? 'none' : 'block' }}>
          <AutoSizer>
            {({ width, height }) => (
              <Table
                width={width}
                height={height}
                rowHeight={50}
                headerHeight={50}
                headerStyle={{ verticalAlign: 'bottom', width: '100%', display: 'flex', justifyContent: 'center' }}
                rowStyle={{ textAlign: 'center' }}
                headerClassName='admin_portal_column_headers'
                rowCount={data?.length || 0}
                rowGetter={({ index }) => data[index]}>
                {cyfAdmin && <Column label='ID' dataKey='id' width={270} cellRenderer={cellValue} />}
                {!ambassadorAdmin && <Column label='Invoice Number' dataKey='number' width={270} cellRenderer={cellValue} />}
                <Column label='Created at' dataKey='created' width={120} flexGrow={0.2} cellRenderer={cellValue} />
                {!ambassadorAdmin && (
                  <Column label='Period' dataKey='period' width={300} flexGrow={0.2} cellRenderer={cellValue} />
                )}
                <Column
                  label='Description'
                  dataKey='description'
                  width={!ambassadorAdmin ? 300 : 300}
                  flexGrow={0.1}
                  cellRenderer={cellValue}
                />
                {!ambassadorAdmin && (
                  <Column label='Total' dataKey='total' width={120} flexGrow={0.2} cellRenderer={cellValue} />
                )}
                {!ambassadorAdmin && <Column label='Status' dataKey='status' width={140} cellRenderer={cellValue} />}
                {!ambassadorAdmin && <Column label='Invoice' dataKey='invoice' width={140} cellRenderer={cellValue} />}
                {!ambassadorAdmin && <Column label='Receipt' dataKey='receipt' width={140} cellRenderer={cellValue} />}
                {ambassadorAdmin && <Column label='Amount' dataKey='amount' width={140} cellRenderer={cellValue} />}
              </Table>
            )}
          </AutoSizer>
        </div>
      </div>
    </section>
  );
};

const mapStateToProps = (state) => {
  const { gettingInvoices, invoices, stripeCustomerId } = state.payment?.stripe || {};
  const { cyfAdmin, ambassadorAdmin } = state.profile?.details || {};
  return {
    ambassadorAdmin,
    currentOrg: state.currentOrganisation,
    branding: state.branding,
    cyfAdmin,
    gettingInvoices,
    invoices,
    stripeCustomerId,
    orgDetails: state.organisation?.details,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getStripeInvoices: () => dispatch(PAYMENT_ACTIONS.getStripeInvoices()),
  getStripePaymentMethods: () => dispatch(PAYMENT_ACTIONS.getStripePaymentMethods()),
  getOrganisation: () => dispatch(ORGANISATION_ACTIONS.getOrganisation()),
});

export default connect(mapStateToProps, mapDispatchToProps)(BillingPage);
